import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type {
  CompanyCloseResult,
  CompanyInviteInput,
  CompanyInviteResult,
  CompanyPendingInvitation,
  CompanyRemovalResult,
  CompanyTeamMember,
  MyCompany,
  MyCompanyInvitation,
  Company,
  CompanyFilters,
  CompanyInput
} from '@/types/companies'

const cleanQuery = (params: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))

/**
 * F-93 1A: companies. The community comes from `X-CommunityId`. Owner and admin
 * manage; finance reads everything; the moderator reads without the contract fields; a member gets 403
 * on the list and reaches only `mine`. Inviting and removing: owner, admin or that company's admin.
 */
export class CompaniesResource {
  constructor(private ctx: SdkContext) {}

  async list(filters: CompanyFilters = {}): Promise<Company[]> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/companies' }, { query: cleanQuery({ ...filters }) })
    return listItems<Company>(result)
  }

  async get(id: string): Promise<Company> {
    return this.ctx.call({ httpMethod: 'GET', path: '/companies/{id}' }, { id })
  }

  async create(body: CompanyInput): Promise<Company> {
    return this.ctx.call({ httpMethod: 'POST', path: '/companies', hasBody: true }, { body })
  }

  /** Only the fields sent change; `endsAt: null` clears the planned exit. */
  async update(id: string, body: CompanyInput): Promise<Company> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/companies/{id}', hasBody: true }, { id, body })
  }

  /** 409 when already closed. `removeMembers` defaults to `true` on the API too. */
  async close(id: string, removeMembers: boolean): Promise<CompanyCloseResult> {
    return this.ctx.call({ httpMethod: 'POST', path: '/companies/{id}/close', hasBody: true }, { id, body: { removeMembers } })
  }

  /** The caller's company and team, or `null` outside any company. */
  async mine(): Promise<MyCompany | null> {
    const result = await this.ctx.call<MyCompany | null>({ httpMethod: 'GET', path: '/companies/mine' })
    return result && typeof result === 'object' && 'company' in result ? result : null
  }

  async team(id: string): Promise<CompanyTeamMember[]> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/companies/{id}/members' }, { id })
    return listItems<CompanyTeamMember>(result)
  }

  async invite(id: string, body: CompanyInviteInput): Promise<CompanyInviteResult> {
    return this.ctx.call({ httpMethod: 'POST', path: '/companies/{id}/members', hasBody: true }, { id, body })
  }

  /** Pending invitations the company admin sent. Community team, and that company's admin while it is active. */
  async invitations(id: string): Promise<CompanyPendingInvitation[]> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/companies/{id}/invitations' }, { id })
    return listItems<CompanyPendingInvitation>(result)
  }

  /** The caller's pending invitations to join a company's team. */
  async myInvitations(): Promise<MyCompanyInvitation[]> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/companies/invitations/mine' })
    return listItems<MyCompanyInvitation>(result)
  }

  /** 404: not the caller's or already answered. 409: `invitation.companyEnded` or `invitation.alreadyInCompany`. */
  async acceptInvitation(invitationId: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/companies/invitations/{invitationId}/accept' }, { invitationId })
  }

  async declineInvitation(invitationId: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/companies/invitations/{invitationId}/decline' }, { invitationId })
  }

  /** Owner and admin only. */
  async setAdmin(id: string, memberId: string, isAdmin: boolean): Promise<CompanyTeamMember> {
    return this.ctx.call(
      { httpMethod: 'PATCH', path: '/companies/{id}/members/{memberId}', hasBody: true },
      { id, memberId, body: { isAdmin } }
    )
  }

  async remove(id: string, memberId: string): Promise<CompanyRemovalResult> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/companies/{id}/members/{memberId}' }, { id, memberId })
  }
}
